import { type VariantProps } from 'class-variance-authority';
import { buttonVariants } from '~/components/ui/button';
import { cn } from '~/lib/utils';

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

/** Legacy aliases mapped onto IDE/shadcn variants (no green CTAs). */
type LegacyVariant = ButtonVariant | 'primary' | 'success' | 'warning';

const legacyToVariant: Record<string, ButtonVariant> = {
  primary: 'default',
  success: 'default',
  warning: 'secondary',
};

const daisyToVariant: Record<string, LegacyVariant> = {
  'btn-ghost': 'ghost',
  'btn-outline': 'outline',
  'btn-alert': 'destructive',
  'btn-error': 'destructive',
  'btn-link': 'link',
  'btn-secondary': 'secondary',
  'btn-neutral': 'default',
  'btn-primary': 'default',
  'btn-success': 'default',
  'btn-warning': 'secondary',
};

const daisyToSize: Record<string, ButtonSize> = {
  'btn-xs': 'xs',
  'btn-sm': 'sm',
  'btn-lg': 'lg',
};

export type ParsedButtonClasses = {
  variant: ButtonVariant;
  size: ButtonSize;
  circle: boolean;
  className: string;
};

function resolveVariant(raw: LegacyVariant): ButtonVariant {
  if (raw === 'primary' || raw === 'success' || raw === 'warning') {
    return legacyToVariant[raw] ?? 'default';
  }
  return raw;
}

/** Map leftover Daisy `btn*` class names to IDE/shadcn-equivalent Tailwind. */
export function parseButtonClasses(className?: string): ParsedButtonClasses {
  const tokens = (className ?? '').split(/\s+/).filter(Boolean);
  let variant: ButtonVariant = 'default';
  let size: ButtonSize = 'default';
  let circle = false;
  const rest: string[] = [];

  for (const token of tokens) {
    if (token === 'btn' || token === 'no-animation') continue;
    if (token === 'btn-circle') {
      circle = true;
      continue;
    }
    if (daisyToVariant[token]) {
      variant = resolveVariant(daisyToVariant[token]!);
      continue;
    }
    if (daisyToSize[token]) {
      size = daisyToSize[token]!;
      continue;
    }
    rest.push(token);
  }

  return { variant, size, circle, className: rest.join(' ') };
}

export function buttonClassName(className?: string) {
  const parsed = parseButtonClasses(className);
  return cn(
    buttonVariants({
      variant: parsed.variant,
      size: parsed.circle ? 'icon' : parsed.size,
    }),
    parsed.circle && 'rounded-full',
    parsed.circle && parsed.size === 'xs' && 'size-6',
    parsed.circle && parsed.size === 'sm' && 'size-8',
    'cursor-pointer',
    parsed.className,
  );
}
